// frontend/src/pages/Dashboard.jsx
// Module 8 update: guided onboarding checklist, trial banner, all module badges live.
// commit: feat(m8): add guided onboarding checklist and trial banner to Dashboard

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import TrialBanner from '../components/TrialBanner';

const PLATFORM_OWNER_ORG_ID = process.env.REACT_APP_PLATFORM_OWNER_ORG_ID;

// ---------------------------------------------------------------------------
// Onboarding checklist steps
// ---------------------------------------------------------------------------
const STEPS = [
  {
    key:    'vendor',
    label:  'Add your first AI vendor',
    detail: 'Register at least one AI system in the Vendor Register.',
    path:   '/vendors',
    cta:    'Go to Vendor Register',
  },
  {
    key:    'control_owner',
    label:  'Assign an owner to a control',
    detail: 'Open a control and set a named owner responsible for evidence.',
    path:   '/controls',
    cta:    'Go to Controls',
  },
  {
    key:    'evidence',
    label:  'Attach evidence to a control',
    detail: 'Upload a document or file as evidence for at least one control.',
    path:   '/controls',
    cta:    'Attach evidence',
  },
  {
    key:    'incident',
    label:  'Log your first incident',
    detail: 'Record an AI-related incident or near-miss in the Incident Register.',
    path:   '/incidents',
    cta:    'Go to Incidents',
  },
  {
    key:    'boardpack',
    label:  'Generate a board pack',
    detail: 'Create a one-click PDF governance report for your board.',
    path:   '/board-packs',
    cta:    'Generate board pack',
  },
  {
    key:    'invite',
    label:  'Invite a team member',
    detail: 'Add a Compliance Manager or Board Member to your account.',
    path:   '/users',
    cta:    'Invite a user',
  },
];

function OnboardingChecklist({ completion, navigate }) {
  const [collapsed, setCollapsed] = useState(false);

  const done  = STEPS.filter(s => completion[s.key]).length;
  const total = STEPS.length;
  const pct   = Math.round((done / total) * 100);
  const allDone = done === total;

  if (allDone) return null;

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      marginBottom: 24,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div
        onClick={() => setCollapsed(c => !c)}
        style={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          borderBottom: collapsed ? 'none' : '1px solid #f3f4f6',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#132e50', marginBottom: 6 }}>
              Get started — {done} of {total} steps complete
            </div>
            {/* Progress bar */}
            <div style={{ width: 200, height: 6, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                width: `${pct}%`,
                height: '100%',
                background: pct === 100 ? '#16a34a' : '#b8860b',
                borderRadius: 4,
                transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
        </div>
        <span style={{ fontSize: 18, color: '#9ca3af', lineHeight: 1 }}>
          {collapsed ? '﹀' : '︿'}
        </span>
      </div>

      {/* Steps */}
      {!collapsed && (
        <div style={{ padding: '4px 0 8px' }}>
          {STEPS.map((step, i) => {
            const isDone = Boolean(completion[step.key]);
            return (
              <div
                key={step.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  padding: '12px 20px',
                  background: isDone ? '#fafafa' : '#fff',
                  borderBottom: i < STEPS.length - 1 ? '1px solid #f9fafb' : 'none',
                  opacity: isDone ? 0.7 : 1,
                }}
              >
                {/* Tick / circle */}
                <div style={{
                  flexShrink: 0,
                  width: 22, height: 22,
                  borderRadius: '50%',
                  background: isDone ? '#16a34a' : '#f3f4f6',
                  border: isDone ? 'none' : '2px solid #d1d5db',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginTop: 1,
                }}>
                  {isDone && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</span>}
                  {!isDone && <span style={{ color: '#9ca3af', fontSize: 10, fontWeight: 700 }}>{i + 1}</span>}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: isDone ? 500 : 600,
                    color: isDone ? '#6b7280' : '#132e50',
                    textDecoration: isDone ? 'line-through' : 'none',
                    marginBottom: 2,
                  }}>
                    {step.label}
                  </div>
                  {!isDone && (
                    <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                      {step.detail}
                    </div>
                  )}
                </div>

                {!isDone && (
                  <button
                    onClick={() => navigate(step.path)}
                    style={{
                      flexShrink: 0,
                      background: 'none',
                      border: '1px solid #132e50',
                      borderRadius: 5,
                      color: '#132e50',
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '5px 12px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {step.cta}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
export default function Dashboard({ user }) {
  const navigate = useNavigate();

  const [vendorSummary, setVendorSummary] = useState(null);
  const [completion,    setCompletion]    = useState({
    vendor:        false,
    control_owner: false,
    evidence:      false,
    incident:      false,
    boardpack:     false,
    invite:        false,
  });

  const isPlatformOwner = user?.org_id === PLATFORM_OWNER_ORG_ID;
  const isAdmin         = user?.role === 'admin';

  useEffect(() => {
    // Vendor summary + completion.vendor
    api.get('/vendors').then(res => {
      const vendors = res.data.vendors || res.data || [];
      setVendorSummary({
        total:       vendors.length,
        remediation: vendors.filter(v => v.cps230_status === 'remediation_required').length,
        overdue:     vendors.filter(v => v.review_overdue).length,
        contracts:   vendors.filter(v => v.contract_expired || v.contract_expiring_soon).length,
      });
      setCompletion(c => ({ ...c, vendor: vendors.length > 0 }));
    }).catch(() => setVendorSummary(null));

    // Controls — owner assigned + evidence attached
    api.get('/controls').then(res => {
      const controls = res.data.controls || res.data || [];
      setCompletion(c => ({
        ...c,
        control_owner: controls.some(ctrl => ctrl.owner_name || ctrl.owner_id),
        evidence:      controls.some(ctrl => ctrl.evidence_file_path || ctrl.has_evidence),
      }));
    }).catch(() => {});

    // Incidents
    api.get('/incidents').then(res => {
      const incidents = res.data.incidents || res.data || [];
      setCompletion(c => ({ ...c, incident: incidents.length > 0 }));
    }).catch(() => {});

    // Board packs
    api.get('/board-packs').then(res => {
      const packs = res.data.packs || res.data || [];
      setCompletion(c => ({ ...c, boardpack: packs.length > 0 }));
    }).catch(() => {});

    // Users — invite step only relevant for admin
    if (isAdmin) {
      api.get('/users').then(res => {
        const users = res.data || [];
        setCompletion(c => ({ ...c, invite: users.length > 1 }));
      }).catch(() => {});
    } else {
      // Non-admin users: mark invite step complete so it doesn't show
      setCompletion(c => ({ ...c, invite: true }));
    }
  }, [isAdmin]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Governance overview</h1>
          <p className="page-subtitle">
            APRA AI Governance Platform · {user?.name || ''}
          </p>
        </div>
      </div>

      {/* Trial banner — hidden for platform owner */}
      {!isPlatformOwner && <TrialBanner user={user} />}

      {/* Onboarding checklist — hidden for platform owner */}
      {!isPlatformOwner && (
        <OnboardingChecklist completion={completion} navigate={navigate} />
      )}

      <div className="module-grid">

        {/* Module 1 — AI Vendor Register — Live */}
        <div
          className="module-card module-card-active"
          onClick={() => navigate('/vendors')}
        >
          <div className="module-card-header">
            <h2>AI Vendor Register</h2>
            <span className="module-badge module-badge-live">Live</span>
          </div>
          <p className="module-desc">
            CPS 230 §25 — Material AI service providers, contract compliance,
            named accountability owners.
          </p>
          {vendorSummary !== null ? (
            <div className="module-stats">
              <span className="module-stat">
                {vendorSummary.total} vendor{vendorSummary.total !== 1 ? 's' : ''} registered
              </span>
              {vendorSummary.remediation > 0 && (
                <span className="module-stat-alert">{vendorSummary.remediation} remediation required</span>
              )}
              {vendorSummary.overdue > 0 && (
                <span className="module-stat-alert">{vendorSummary.overdue} review{vendorSummary.overdue !== 1 ? 's' : ''} overdue</span>
              )}
              {vendorSummary.contracts > 0 && (
                <span className="module-stat-alert">{vendorSummary.contracts} contract issue{vendorSummary.contracts !== 1 ? 's' : ''}</span>
              )}
              {vendorSummary.total === 0 && (
                <span className="module-cta">Add your first vendor →</span>
              )}
            </div>
          ) : (
            <div className="module-stats"><span className="module-stat">Loading…</span></div>
          )}
          <div className="module-cta">Open register →</div>
        </div>

        {/* Module 2 — Control Evidence Locker — Live */}
        <div
          className="module-card module-card-active"
          onClick={() => navigate('/controls')}
        >
          <div className="module-card-header">
            <h2>Control Evidence Locker</h2>
            <span className="module-badge module-badge-live">Live</span>
          </div>
          <p className="module-desc">
            CPS 230 — Operationalised governance controls with evidence uploads,
            named owners, and review dates.
          </p>
          <div className="module-cta">Open controls →</div>
        </div>

        {/* Module 4 — AI Incident Register — Live */}
        <div
          className="module-card module-card-active"
          onClick={() => navigate('/incidents')}
        >
          <div className="module-card-header">
            <h2>AI Incident Register</h2>
            <span className="module-badge module-badge-live">Live</span>
          </div>
          <p className="module-desc">
            CPS 230 continuous monitoring — AI-specific incident taxonomy,
            severity, notification obligations, and resolution tracking.
          </p>
          <div className="module-cta">Open register →</div>
        </div>

        {/* Module 3 — Board Pack Generator — Live */}
        <div
          className="module-card module-card-active"
          onClick={() => navigate('/board-packs')}
        >
          <div className="module-card-header">
            <h2>Board Pack Generator</h2>
            <span className="module-badge module-badge-live">Live</span>
          </div>
          <p className="module-desc">
            One-click PDF board paper from live platform data — control status,
            vendor risk posture, open incidents, attestation block.
          </p>
          <div className="module-cta">Generate pack →</div>
        </div>

      </div>
    </div>
  );
}
